import React from 'react'
import { filterBy, orderBy, translateDataSourceResultGroups } from '@progress/kendo-data-query'
import { convertIsoStringsToDates } from '@/utils/dateUtilities'
import * as ReactDOM from 'react-dom'
import LoadingIndicator from '@/components/LoadingIndicator'
import cloneDeep from 'lodash/cloneDeep'
import { CancelToken } from '@/utils/fetch'
import { localized } from '@/decorators/localized'
import pascalCase from '@/utils/pascalCase'
import { isDefinedAndNotEmpty } from '@/utils/isDefined'

@localized()
class ServerData extends React.PureComponent {
  state = {
    loading: false
  }

  componentDidUpdate (prevProps) {
    if (this.state.loading) { return }

    if (hasChange(prevProps, this.props)) {
      if (this.props.dataState.filter !== prevProps.dataState.filter) {
        // delayed filtering
        if (this._handle) clearTimeout(this._handle)
        this._handle = setTimeout(() => this.processData(this.props), 500)
      } else {
        if (this._handle) clearTimeout(this._handle)
        this.processData(this.props)
      }
    }
  }

  componentDidMount () {
    this.processData(this.props)
  }

  componentWillUnmount () {
    this._disposed = true
    this.cancel.cancel()
  }

  processData (props) {
    const { dataState = {}, dataSource, onLoad, dateTimeProperties, columnTypes } = props

    const hasGroups = dataState.group && dataState.group.length

    this.setState({ loading: true })

    this.props.onLoading()

    if (this.cancel) this.cancel.cancel()

    this.cancel = CancelToken.source()

    const getAllDataState = { ...dataState, skip: 0, page: 0, take: 0 }

    dataSource(fixDataState(props.getAllData ? getAllDataState : dataState, columnTypes, this.props), { cancelToken: this.cancel.token })
      .then(response => transformDateTime(response, dateTimeProperties))
      .then(({ data, total }) => {
        if (this._disposed) return
        this.setState({ loading: false })
        onLoad({
          data: hasGroups ? translateDataSourceResultGroups(data) : data,
          total
        })
      })
      .catch((error) => {
        if (this._disposed) return
        this.setState({ loading: false })
        onLoad({
          error: error,
          data: [],
          total: 0
        })
      })
  }

  render () {
    if (this.state.loading || this.props.loading) {
      const loadingPanel = <LoadingIndicator />
      const grid = this.props.getGridWrapperElement()
      const gridContent = grid && grid.querySelector('.k-grid-content')
      return gridContent ? ReactDOM.createPortal(loadingPanel, gridContent) : loadingPanel
    }
    return null
  }
}

@localized()
class MemoryData extends React.PureComponent {
  componentDidUpdate (prevProps) {
    if (hasChange(prevProps, this.props)) {
      this.processData(this.props)
    }
  }

  componentDidMount () {
    this.processData(this.props)
  }

  processData (props) {
    props.onLoad(processDataStateInMemory(props))
  }

  render () {
    if (this.props.loading) {
      const loadingPanel = <LoadingIndicator />
      const grid = this.props.getGridWrapperElement()
      const gridContent = grid && grid.querySelector('.k-grid-content')
      return gridContent ? ReactDOM.createPortal(loadingPanel, gridContent) : loadingPanel
    }
    return null
  }
}

export function processDataStateInMemory (props) {
  const { dataState = {}, items, getAllData } = props
  let { skip, take, filter, sort } = dataState

  if (skip >= items.length) {
    skip = 0
  }

  let processedItems = items
  const fixedFilter = fixDataState({ filter, sort }, props.columnTypes, props, true)
  if (filter && !getAllData) {
    processedItems = filterBy(processedItems, fixedFilter.filter)
  }
  if (sort) {
    processedItems = orderBy(processedItems, fixedFilter.sort || [])
  }

  return {
    data: getAllData ? processedItems : processedItems.slice(skip, skip + take),
    total: processedItems.length
  }
}

function hasChange (props, newProps) {
  return newProps.dataState !== props.dataState ||
    (newProps.items !== props.items && ((newProps.items && newProps.items.length > 0) || (props.items && props.items.length > 0))) ||
    props.lastReloadedAt !== newProps.lastReloadedAt
}

const transformDateTime = (response, propertyNames = []) => {
  return new Promise((resolve) => {
    if (propertyNames.length === 0) {
      resolve(response)
    }

    const data = response.data.map(item => convertIsoStringsToDates(item, propertyNames))
    resolve({ data, total: response.total })
  })
}

export function fixDataState (data, columnTypes, props, isInMemory) {
  data = cloneDeep(data)
  if (data.filter && data.filter.filters && data.filter.filters.length) {
    data.filter.filters = data.filter.filters
      .filter(x => ['isnull', 'isnotnull', 'isempty', 'isnotempty'].includes(x.operator) ||
        isDefinedAndNotEmpty(x.value))

    data.filter.filters.forEach(x => {
      const columnType = columnTypes[x.field]
      if (columnType === 'lookup') {
        if (typeof x.value === 'object' && x.value.hasOwnProperty('id')) {
          if (isInMemory) {
            x.field = `${x.field}.id`
          } else {
            x.field = `${x.field}Id`
          }

          x.value = x.value.id
        }
      } else if (x.operator === 'between') {
        const start = x.value[0]
        const end = x.value[1]
        x.logic = 'and'
        x.filters = [
          !!start && { field: x.field, operator: 'gte', value: start.toISOString() },
          !!end && { field: x.field, operator: 'lte', value: end.toISOString() }
        ].filter(Boolean)
      } else if (x.operator === 'isempty') {
        x.logic = 'or'
        x.filters = [
          { field: x.field, operator: 'isnull', value: x.value }
        ]
      } else if (x.operator === 'isnotempty') {
        x.logic = 'and'
        x.filters = [
          { field: x.field, operator: 'isnotnull', value: x.value }
        ]
      }
    })
  }
  data.sort && data.sort.forEach(x => {
    const columnType = columnTypes[x.field]
    if (columnType === 'lookup') {
      if (isInMemory) {
        x.field = x.field + '.' + props.localizeProps()
      } else {
        x.field = x.field + pascalCase(props.localizeProps())
      }
    }
  })
  return data
}

export {
  ServerData,
  MemoryData,
  transformDateTime
}



// WEBPACK FOOTER //
// ./src/components/BsrsGrid/dataProviders.js